import { useCallback, useEffect, useState } from "react";
import { X, CheckCircle, XCircle, AlertTriangle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export type ToastType = "error" | "warning" | "info" | "success";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  type: ToastType;
  title: string;
  message: string;
  autoHide: boolean;
  action?: ToastAction;
  onClose: () => void;
}

const iconMap = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const styleMap = {
  success: "border-green-500 bg-green-50 text-green-900",
  error: "border-red-500 bg-red-50 text-red-900",
  warning: "border-amber-500 bg-amber-50 text-amber-900",
  info: "border-blue-500 bg-blue-50 text-blue-900",
};

const iconColorMap = {
  success: "text-green-600",
  error: "text-red-600",
  warning: "text-amber-600",
  info: "text-blue-600",
};

const AUTO_HIDE_DELAY = 5000;

const Toast = ({ type, title, message, autoHide, action, onClose }: ToastProps) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  const Icon = iconMap[type];

  useEffect(() => {
    // Trigger enter animation
    const enterTimer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(enterTimer);
  }, []);

  const handleClose = useCallback(() => {
    setExiting(true);
    setTimeout(() => onClose(), 300);
  }, [onClose]);

  useEffect(() => {
    if (!autoHide) return;
    const timer = setTimeout(() => handleClose(), AUTO_HIDE_DELAY);
    return () => clearTimeout(timer);
  }, [autoHide, handleClose]);

  return (
    <div
      className={cn(
        "pointer-events-auto flex w-80 items-start gap-3 rounded-lg border-l-4 p-4 shadow-lg transition-all duration-300",
        styleMap[type],
        visible && !exiting
          ? "translate-x-0 opacity-100"
          : "translate-x-full opacity-0"
      )}
    >
      <Icon className={cn("h-5 w-5 mt-0.5 shrink-0", iconColorMap[type])} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-sm mt-1 opacity-90">{message}</p>
        {action && (
          <button
            onClick={action.onClick}
            className="mt-2 text-sm font-medium underline underline-offset-2 hover:opacity-80 transition-opacity"
          >
            {action.label}
          </button>
        )}
      </div>
      <button
        onClick={handleClose}
        className="shrink-0 rounded p-1 opacity-60 hover:opacity-100 transition-opacity"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default Toast;
